import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Confirm,
  Container,
  Dropdown,
  Icon,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Popup,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import { useBulkUpdateAutoTranscribe } from "../../../hooks/useBulkUpdateAutoTranscribe";
import { useBulkUpdateMeetingNotesTemplate } from "../../../hooks/useBulkUpdateMeetingNotesTemplate";
import { useBulkUpdateTimezone } from "../../../hooks/useBulkUpdateTimezone";
import { useMeetingNotesTemplates } from "../../../hooks/useMeetingNotesTemplates";
import { useUpdateUser } from "../../../hooks/useUpdateUser";
import { style } from "../../../style";
import { useAuth } from "../../../contexts/AuthContext";
import { getTimezoneOptions } from "../../../utils/timezoneOptions";

// Sentinel for "no personal override, inherit the customer default". It has
// to be a non-empty string: Semantic UI's Dropdown treats "" as "no value at
// all" and keeps showing the placeholder. Sent to the API as null.
const INHERIT_DEFAULT = "__customer_default__";

const UsersTable = ({ data }) => {
  const { hasFeature } = useAuth();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [timezoneModalOpen, setTimezoneModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const [
    bulkAutoTranscribeConfirmationOpen,
    setBulkAutoTranscribeConfirmationOpen,
  ] = useState(false);
  const [userToToggle, setUserToToggle] = useState({});
  const [licenceStatus, setLicenceStatus] = useState();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkTimezone, setBulkTimezone] = useState("");
  const [bulkAutoTranscribe, setBulkAutoTranscribe] = useState(false);
  // null = nothing picked yet (Apply stays disabled); INHERIT_DEFAULT is a
  // deliberate pick of "Customer default", a valid, meaningful choice here.
  const [bulkTemplateId, setBulkTemplateId] = useState(null);

  const navigate = useNavigate();

  const { templates } = useMeetingNotesTemplates();
  // INHERIT_DEFAULT means "no personal override, inherit the customer
  // default", which is not the same as SummariseTemplatePicker's BUILT_IN:
  // that one forces the built-in prompt. Both reach the API as null.
  const templateOptions = useMemo(
    () => [
      {
        key: INHERIT_DEFAULT,
        value: INHERIT_DEFAULT,
        text: "Customer default",
      },
      ...templates.map((template) => ({
        key: template.id,
        value: template.id,
        text: template.name,
      })),
    ],
    [templates],
  );
  const templateNameById = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.id, t.name])),
    [templates],
  );

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const { updateUserMutation } = useUpdateUser(() => navigate("/admin/users"));

  const { bulkUpdateTimezoneMutation } = useBulkUpdateTimezone(() => {
    setSelectedUserIds([]);
    setBulkTimezone("");
  });

  const { bulkUpdateAutoTranscribeMutation } = useBulkUpdateAutoTranscribe(
    () => {
      setSelectedUserIds([]);
      setBulkAutoTranscribe(false);
    },
  );

  const { bulkUpdateMeetingNotesTemplateMutation } =
    useBulkUpdateMeetingNotesTemplate(() => {
      setSelectedUserIds([]);
      setBulkTemplateId(null);
    });

  useEffect(() => {
    setSelectedUserIds([]);
    setBulkTimezone("");
  }, [data]);

  const allUsersSelected =
    data?.length > 0 && selectedUserIds.length === data.length;
  const someUsersSelected = selectedUserIds.length > 0 && !allUsersSelected;
  const isBusy =
    updateUserMutation.isLoading ||
    bulkUpdateTimezoneMutation.isLoading ||
    bulkUpdateAutoTranscribeMutation.isLoading ||
    bulkUpdateMeetingNotesTemplateMutation.isLoading;

  const handleRemoveLicence = (user, toggleStatus) => {
    setConfirmationOpen(false);
    const userId = user.id;
    const userData = {
      email: user.email,
      ipecs_ext: user.ipecs_ext,
      user_timezone: user.user_timezone,
      has_sidekick: toggleStatus,
      admin: user.is_admin,
    };
    updateUserMutation.mutate({ userId, userData });
  };

  const handleToggleLicence = (user, toggleStatus) => {
    setUserToToggle(user);
    setLicenceStatus(toggleStatus);
    if (user.has_connect) {
      // Connect user: disable licence without prompt
      handleRemoveLicence(user, toggleStatus);
    } else {
      // Sidekick user: prompt to delete user
      setUserToToggle(user);
      setLicenceStatus(toggleStatus);
      setConfirmationOpen(true);
    }
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUserIds((previous) => {
      if (checked) {
        return Array.from(new Set([...previous, userId]));
      }
      return previous.filter((id) => id !== userId);
    });
  };

  const handleSelectAll = (checked) => {
    if (!data?.length) {
      return;
    }
    setSelectedUserIds(checked ? data.map((user) => user.id) : []);
  };

  const handleResetTimezones = () => {
    if (!selectedUserIds.length) {
      return;
    }
    setResetConfirmationOpen(true);
  };

  const handleConfirmResetTimezones = () => {
    setResetConfirmationOpen(false);
    bulkUpdateTimezoneMutation.mutate({
      userSqids: selectedUserIds,
      timezone: null,
    });
  };

  const handleConfirmBulkAutoTranscribe = () => {
    setBulkAutoTranscribeConfirmationOpen(false);
    bulkUpdateAutoTranscribeMutation.mutate({
      userSqids: selectedUserIds,
      autoTranscribe: bulkAutoTranscribe,
    });
  };

  const handleConfirmBulkTimezone = () => {
    setTimezoneModalOpen(false);
    bulkUpdateTimezoneMutation.mutate({
      userSqids: selectedUserIds,
      timezone: bulkTimezone,
    });
  };

  const handleConfirmBulkTemplate = () => {
    setTemplateModalOpen(false);
    bulkUpdateMeetingNotesTemplateMutation.mutate({
      userSqids: selectedUserIds,
      templateId: bulkTemplateId === INHERIT_DEFAULT ? null : bulkTemplateId,
    });
  };

  return (
    <Container style={{ marginTop: "20px" }}>
      <div
        style={{
          display: selectedUserIds.length > 0 ? "block" : "none",
          minHeight: "52px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            visibility: selectedUserIds.length > 0 ? "visible" : "hidden",
          }}
        >
          <Label
            color="teal"
            style={{ minWidth: "120px", textAlign: "center" }}
          >
            {selectedUserIds.length} selected
          </Label>
          <Dropdown
            text="Actions"
            className="button right labeled icon"
            icon="caret down"
            disabled={isBusy}
            style={style.gradientButton}
          >
            <Dropdown.Menu>
              <Dropdown.Item
                icon="clock"
                text="Set timezone…"
                onClick={() => setTimezoneModalOpen(true)}
              />
              <Dropdown.Item
                icon="undo"
                text="Reset timezones"
                onClick={handleResetTimezones}
              />
              <Dropdown.Item
                icon="file alternate outline"
                text="Set notes template…"
                onClick={() => setTemplateModalOpen(true)}
              />
              <Dropdown.Divider />
              {hasFeature(["auto_transcribe"]) && (
                <>
                  <Dropdown.Item
                    icon="check"
                    text="Enable auto transcribe"
                    onClick={() => {
                      setBulkAutoTranscribe(true);
                      setBulkAutoTranscribeConfirmationOpen(true);
                    }}
                  />
                  <Dropdown.Item
                    icon="close"
                    text="Disable auto transcribe"
                    onClick={() => {
                      setBulkAutoTranscribe(false);
                      setBulkAutoTranscribeConfirmationOpen(true);
                    }}
                  />
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <Button
            onClick={() => setSelectedUserIds([])}
            disabled={isBusy}
            style={style.gradientButtonInactive}
          >
            Clear selection
          </Button>
        </div>
      </div>
      {!hasFeature(["auto_transcribe"]) && (
        <div>
          Auto transcribe can't be enabled for your account, speak to your
          provider in order to enable this.
        </div>
      )}
      {data?.length === 0 ? (
        <p>No users found</p>
      ) : (
        <Table basic="very" inverted>
          <TableHeader>
            <TableRow>
              <TableHeaderCell width={1}>
                <Checkbox
                  className="bulk-selection-checkbox"
                  checked={allUsersSelected}
                  indeterminate={someUsersSelected}
                  onChange={(_, checkboxData) => {
                    handleSelectAll(checkboxData.checked);
                  }}
                  disabled={isBusy}
                />
              </TableHeaderCell>
              <TableHeaderCell width={4}>Name</TableHeaderCell>
              <TableHeaderCell width={2}>Extension</TableHeaderCell>
              <TableHeaderCell width={3}>Timezone</TableHeaderCell>
              <TableHeaderCell width={2} textAlign="center">
                Auto transcribe
              </TableHeaderCell>
              <TableHeaderCell width={3}>Notes template</TableHeaderCell>
              <TableHeaderCell width={3}>Licence</TableHeaderCell>
              <TableHeaderCell width={2} textAlign="left">
                <span
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  Sidekick user{" "}
                  <Popup
                    content="Disabling a user will allow you to assign the licence to another user. If the user doesn't also have a Connect licence, disabling them will delete their account."
                    basic
                    trigger={<Icon name="info circle" size="small" />}
                  />
                </span>
              </TableHeaderCell>
              <TableHeaderCell width={2}></TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((user) => {
              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <Checkbox
                      className="bulk-selection-checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={(_, checkboxData) => {
                        handleSelectUser(user.id, checkboxData.checked);
                      }}
                      disabled={isBusy}
                    />
                  </TableCell>
                  <TableCell>
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell>{user.ipecs_ext}</TableCell>
                  <TableCell>
                    {user.effective_timezone ? (
                      <Popup
                        content={`Current time: ${moment()
                          .tz(user.effective_timezone)
                          .format("h:mm A")}`}
                        basic
                        position="top center"
                        trigger={<span>{user.effective_timezone}</span>}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell textAlign="center">
                    {user.auto_transcribe ? (
                      <Icon name="check" color="green" />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {/* A lookup miss means the templates haven't loaded (or
                        couldn't be loaded), never that the user inherits:
                        deleting a template nulls its users server-side. */}
                    {user.meeting_notes_template_id
                      ? (templateNameById[user.meeting_notes_template_id] ??
                        "-")
                      : "Customer default"}
                  </TableCell>
                  <TableCell>
                    {user.has_connect ? (
                      <Label color="blue" horizontal>
                        Connect
                      </Label>
                    ) : (
                      <Label color="purple" horizontal>
                        Sidekick
                      </Label>
                    )}
                  </TableCell>
                  <TableCell textAlign="left">
                    <Checkbox
                      toggle
                      checked={user.has_sidekick}
                      disabled={isBusy}
                      onChange={(_, data) => {
                        handleToggleLicence(user, data.checked);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    textAlign="right"
                    style={{
                      gap: "25px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Popup
                      position="bottom center"
                      content={"Edit user"}
                      basic
                      trigger={
                        <Link to={`/admin/users/${user.id}`}>
                          <Button
                            icon
                            disabled={isBusy}
                            onClick={() => {}}
                            style={style.purpleGradientButton}
                          >
                            <Icon name="edit" />
                          </Button>
                        </Link>
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <DeleteConfirmation
        open={confirmationOpen}
        user={userToToggle}
        licenceStatus={licenceStatus}
        onConfirm={handleRemoveLicence}
        onCancel={() => setConfirmationOpen(false)}
      />
      <Modal
        size="tiny"
        open={timezoneModalOpen}
        onClose={() => {
          setTimezoneModalOpen(false);
          setBulkTimezone("");
        }}
      >
        <ModalHeader>Set timezone</ModalHeader>
        <ModalContent>
          <p>
            Select a timezone to apply to {selectedUserIds.length} user
            {selectedUserIds.length === 1 ? "" : "s"}.
          </p>
          <Dropdown
            search
            selection
            fluid
            clearable
            options={timezoneOptions}
            value={bulkTimezone}
            placeholder="Select a timezone"
            onChange={(_, { value }) => setBulkTimezone(value || "")}
          />
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setTimezoneModalOpen(false);
              setBulkTimezone("");
            }}
          >
            Cancel
          </Button>
          <Button
            style={style.gradientButton}
            disabled={!bulkTimezone}
            loading={bulkUpdateTimezoneMutation.isLoading}
            onClick={handleConfirmBulkTimezone}
          >
            Apply
          </Button>
        </ModalActions>
      </Modal>
      <Modal
        size="tiny"
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setBulkTemplateId(null);
        }}
      >
        <ModalHeader>Set notes template</ModalHeader>
        <ModalContent>
          <p>
            Select a meeting notes template to apply to {selectedUserIds.length}{" "}
            user{selectedUserIds.length === 1 ? "" : "s"}.
          </p>
          <Dropdown
            search
            selection
            fluid
            selectOnBlur={false}
            options={templateOptions}
            value={bulkTemplateId}
            placeholder="Select a template"
            onChange={(_, { value }) => setBulkTemplateId(value)}
          />
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setTemplateModalOpen(false);
              setBulkTemplateId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            style={style.gradientButton}
            disabled={bulkTemplateId === null}
            loading={bulkUpdateMeetingNotesTemplateMutation.isLoading}
            onClick={handleConfirmBulkTemplate}
          >
            Apply
          </Button>
        </ModalActions>
      </Modal>
      <ResetTimezoneConfirmation
        open={resetConfirmationOpen}
        userCount={selectedUserIds.length}
        onCancel={() => setResetConfirmationOpen(false)}
        onConfirm={handleConfirmResetTimezones}
      />
      <BulkAutoTranscriptionConfirmation
        open={bulkAutoTranscribeConfirmationOpen}
        enabled={bulkAutoTranscribe}
        userCount={selectedUserIds.length}
        onCancel={() => setBulkAutoTranscribeConfirmationOpen(false)}
        onConfirm={handleConfirmBulkAutoTranscribe}
      />
    </Container>
  );
};

const DeleteConfirmation = ({
  open,
  user,
  licenceStatus,
  onConfirm,
  onCancel,
}) => (
  <Confirm
    open={open}
    header="Delete user?"
    content={`${user.first_name} ${user.last_name} only has a Sidekick licence, so deactivating the licence will delete their account and all associated transcription records.`}
    confirmButton="Delete"
    onCancel={onCancel}
    onConfirm={() => onConfirm(user, licenceStatus)}
  />
);

const ResetTimezoneConfirmation = ({
  open,
  userCount,
  onCancel,
  onConfirm,
}) => (
  <Confirm
    open={open}
    header="Reset timezones?"
    content={`This will reset the timezone for ${userCount} user${
      userCount === 1 ? "" : "s"
    } back to the customer's default timezone.`}
    confirmButton="Reset"
    onCancel={onCancel}
    onConfirm={onConfirm}
  />
);

const BulkAutoTranscriptionConfirmation = ({
  open,
  enabled,
  userCount,
  onCancel,
  onConfirm,
}) => (
  <Confirm
    open={open}
    header="Update auto transcribe?"
    content={`Do you want to ${enabled ? "enable" : "disable"} auto transcribe for ${userCount} user${
      userCount === 1 ? "" : "s"
    }?`}
    confirmButton="Apply"
    onCancel={onCancel}
    onConfirm={onConfirm}
  />
);

export default UsersTable;
